import { Plus, Trash2 } from "lucide-react";

// Resource Costs — what it costs US to deliver the project. Three cost types:
// Fixed (flat amount), Hourly (rate × hours) and % of Revenue (of the project
// value). The summary shows Project Value − Total Cost = Estimated Net Value.

export interface ResourceCost {
  _id?: string;
  label: string;
  type: "fixed" | "hourly" | "percentage";
  amount: number; // fixed: amount · hourly: rate · percentage: percent
  hours?: number;
}

interface Props {
  costs: ResourceCost[];
  onChange: (costs: ResourceCost[]) => void;
  projectValue: number;
}

export const resourceCostTotal = (costs: ResourceCost[], projectValue: number) =>
  (costs || []).reduce((sum, c) => {
    if (c.type === "hourly") return sum + (Number(c.amount) || 0) * (Number(c.hours) || 0);
    if (c.type === "percentage") return sum + (projectValue * (Number(c.amount) || 0)) / 100;
    return sum + (Number(c.amount) || 0);
  }, 0);

const money = (n: number) => `$${(Math.round(n * 100) / 100).toLocaleString()}`;

const ResourceCostsEditor = ({ costs, onChange, projectValue }: Props) => {
  const addRow = () => onChange([...costs, { label: "", type: "fixed", amount: 0, hours: 0 }]);
  const updateRow = (i: number, patch: Partial<ResourceCost>) =>
    onChange(costs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const removeRow = (i: number) => onChange(costs.filter((_, idx) => idx !== i));

  const totalCost = resourceCostTotal(costs, projectValue);
  const net = projectValue - totalCost;

  const rowTotal = (c: ResourceCost) =>
    c.type === "hourly"
      ? (Number(c.amount) || 0) * (Number(c.hours) || 0)
      : c.type === "percentage"
      ? (projectValue * (Number(c.amount) || 0)) / 100
      : Number(c.amount) || 0;

  return (
    <div className="rounded-xl border border-outline-variant p-3.5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-[13px] font-semibold text-on-surface">Resource Costs</h4>
          <p className="text-[11px] text-on-surface-variant">Costs of delivering the project — used for profitability tracking.</p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <Plus size={13} /> Add Cost
        </button>
      </div>

      {costs.length > 0 && (
        <div className="space-y-1.5">
          {costs.map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 flex-wrap">
              <select
                value={c.type}
                onChange={(e) => updateRow(i, { type: e.target.value as any })}
                className="shrink-0 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="fixed">Fixed</option>
                <option value="hourly">Hourly</option>
                <option value="percentage">% of Revenue</option>
              </select>
              <input
                placeholder="Cost description (e.g. Developer, Hosting)"
                className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                value={c.label}
                onChange={(e) => updateRow(i, { label: e.target.value })}
              />
              <input
                type="number"
                min={0}
                placeholder={c.type === "hourly" ? "Rate $/hr" : c.type === "percentage" ? "%" : "Amount $"}
                title={c.type === "hourly" ? "Hourly rate" : c.type === "percentage" ? "Percentage of the project value" : "Fixed amount"}
                className="w-24 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white text-right"
                value={c.amount || ""}
                onChange={(e) => updateRow(i, { amount: Number(e.target.value) || 0 })}
              />
              {c.type === "hourly" && (
                <input
                  type="number"
                  min={0}
                  placeholder="Hours"
                  className="w-20 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white text-right"
                  value={c.hours || ""}
                  onChange={(e) => updateRow(i, { hours: Number(e.target.value) || 0 })}
                />
              )}
              <span className="w-20 text-right text-[12px] font-semibold text-gray-700 shrink-0">{money(rowTotal(c))}</span>
              <button type="button" onClick={() => removeRow(i)} className="shrink-0 p-1.5 rounded-md border border-gray-200 text-red-500 hover:bg-red-50">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 pt-1">
        <div className="rounded-lg bg-gray-50 border border-gray-100 p-2">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Project Value</p>
          <p className="text-[13px] font-extrabold text-gray-800">{money(projectValue)}</p>
        </div>
        <div className="rounded-lg bg-gray-50 border border-gray-100 p-2">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Resource Cost</p>
          <p className="text-[13px] font-extrabold text-gray-800">{money(totalCost)}</p>
        </div>
        <div className={`rounded-lg border p-2 ${net < 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-100"}`}>
          <p className={`text-[10px] font-bold uppercase tracking-wider ${net < 0 ? "text-red-500" : "text-emerald-600"}`}>Estimated Net Value</p>
          <p className={`text-[13px] font-extrabold ${net < 0 ? "text-red-700" : "text-emerald-700"}`}>{money(net)}</p>
        </div>
      </div>
    </div>
  );
};

export default ResourceCostsEditor;
